import { useEffect, useState } from "react";
import { fetchWeatherData, type WeatherData } from "@/services/weatherService";
import { getWeatherIcon } from "@/utils/weatherIcons";
import { useLanguage } from '@/contexts/LanguageContext';

const WEATHER_CACHE_TTL_MS = 10 * 60 * 1000;

interface CachedWeather {
  data: WeatherData;
  fetchedAt: number;
}

const weatherMemoryCache = new Map<string, CachedWeather>();
const pendingWeatherRequests = new Map<string, Promise<WeatherData>>();

const weatherCacheKey = (location: string, lat: number, lon: number) => (
  `${location.trim().toLowerCase()}|${lat}|${lon}`
);

const readCachedWeather = (key: string): CachedWeather | null => {
  const memoryValue = weatherMemoryCache.get(key);
  if (memoryValue) return memoryValue;
  try {
    const storedValue = sessionStorage.getItem(`weather-cache:${key}`);
    if (!storedValue) return null;
    const parsed = JSON.parse(storedValue) as CachedWeather;
    if (!parsed?.data || typeof parsed.fetchedAt !== 'number') return null;
    weatherMemoryCache.set(key, parsed);
    return parsed;
  } catch {
    return null;
  }
};

const storeCachedWeather = (key: string, data: WeatherData) => {
  const cached = { data, fetchedAt: Date.now() };
  weatherMemoryCache.set(key, cached);
  try {
    sessionStorage.setItem(`weather-cache:${key}`, JSON.stringify(cached));
  } catch {
    // El estado en memoria sigue evitando parpadeos aunque sessionStorage no esté disponible.
  }
};

interface WeatherWidgetProps {
  location: string;
  lat: number;
  lon: number;
  apiKey?: string;
}

export const WeatherWidget = ({ location, lat, lon, apiKey }: WeatherWidgetProps) => {
  const { language, t } = useLanguage();
  const cacheKey = `${weatherCacheKey(location, lat, lon)}|${language}`;
  const [weather, setWeather] = useState<WeatherData>(() => (
    readCachedWeather(cacheKey)?.data ?? {
      location,
      description: "Cargando...",
      temp: 0,
      weatherCode: 800,
    }
  ));
  const [loading, setLoading] = useState(() => !readCachedWeather(cacheKey));

  useEffect(() => {
    let cancelled = false;

    const loadWeather = async () => {
      const cached = readCachedWeather(cacheKey);
      if (cached) {
        setWeather(cached.data);
        setLoading(false);
        if (Date.now() - cached.fetchedAt < WEATHER_CACHE_TTL_MS) return;
      } else {
        setWeather({ location, description: "Cargando...", temp: 0, weatherCode: 800 });
        setLoading(true);
      }

      try {
        let request = pendingWeatherRequests.get(cacheKey);
        if (!request) {
          request = fetchWeatherData(location, lat, lon, apiKey, language);
          pendingWeatherRequests.set(cacheKey, request);
        }
        const data = await request;
        storeCachedWeather(cacheKey, data);
        if (!cancelled) setWeather(data);
      } catch (error) {
        if (!cancelled && !cached) {
          const fallbackWeather: WeatherData = {
            location,
            description: { es: 'Parcialmente nublado', en: 'Partly cloudy', fr: 'Partiellement nuageux', de: 'Teilweise bewölkt' }[language],
            temp: 18,
            weatherCode: 801,
          };
          storeCachedWeather(cacheKey, fallbackWeather);
          setWeather(fallbackWeather);
        }
      } finally {
        pendingWeatherRequests.delete(cacheKey);
        if (!cancelled) setLoading(false);
      }
    };

    void loadWeather();
    return () => { cancelled = true; };
  }, [apiKey, cacheKey, language, lat, location, lon]);

  const WeatherIcon = getWeatherIcon(weather.weatherCode);
  const weatherTone = weather.weatherCode >= 200 && weather.weatherCode < 300
    ? "is-storm"
    : weather.weatherCode >= 300 && weather.weatherCode < 600
      ? "is-rain"
      : weather.weatherCode === 800
        ? "is-sunny"
        : "is-cloudy";

  return (
    <section className={`weather-widget ${weatherTone}`} aria-label={t('weather.info')}>
      <div className="weather-wave weather-wave-a" />
      <div className="weather-wave weather-wave-b" />
      <div className="weather-left">
        <div className="weather-location">{weather.location}</div>
        <div className="weather-desc">
          {loading ? t('weather.loading') : weather.description}
        </div>
      </div>
      <div className="weather-right">
        <div className="weather-icon-wrap">
          <WeatherIcon className="weather-icon" strokeWidth={1.8} />
        </div>
        <div className="weather-temp">{weather.temp}°</div>
      </div>
    </section>
  );
};
